export interface SessionUser {
  id: string;
  email: string;
  displayName?: string | null;
  baseCurrency: string;
  sellerType?: string;
  tier: string;
  emailVerified: boolean;
}

export interface DashboardPayload {
  generatedAt: number;
  baseCurrency: string;
  performance: {
    current: {
      sales: {
        count: number;
        revenueBaseAmountMinor: number;
        averageSaleBaseAmountMinor: number | null;
      };
      operatingCosts: { totalBaseAmountMinor: number };
      netProfitBaseAmountMinor: number;
    };
    change: {
      revenue: { changePercent: number | null };
      netProfit: { changePercent: number | null };
      salesCount: { changePercent: number | null };
    } | null;
  };
  inventory: {
    itemCount: number;
    holdingCount: number;
    totalCostBaseAmountMinor: number;
    marketBaseAmountMinor: number;
    potentialProfitBaseAmountMinor: number;
    roiPercent: number | null;
    valuationCoveragePercent: number | null;
    listings: {
      activeCount: number;
      draftCount: number;
      expiredCount: number;
    };
    ageing: { overThresholdHoldingCount: number };
  };
  trend: Array<{
    bucketStart: number;
    revenueBaseAmountMinor: number;
    netProfitBaseAmountMinor: number;
  }>;
  recentSales: {
    items: Array<{
      id: string;
      item: { name: string | null };
      marketplaceName: string | null;
      soldAt: number;
      status: "completed" | "returned";
      revenueBaseAmountMinor: number;
      netProfitBaseAmountMinor: number;
    }>;
  };
  attention: Array<{
    code: string;
    severity: "action" | "warning" | "info";
    count: number;
  }>;
}

interface ApiErrorBody {
  error?: { message?: string };
}

export const PUBLIC_AUTH_ENABLED =
  import.meta.env.VITE_PUBLIC_AUTH_ENABLED === "true";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  if (!PUBLIC_AUTH_ENABLED) {
    if (path === "/auth/me") {
      throw new ApiRequestError("No active account session.", 401);
    }
    throw new ApiRequestError(
      "Account access is not active in this public v1 yet. No credentials were sent or stored.",
      503,
    );
  }
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body === undefined
        ? {}
        : { "Content-Type": "application/json" }),
      ...init.headers,
    },
  });

  if (!response.ok) {
    let message = "Something went wrong. Please try again.";
    try {
      const body = (await response.json()) as ApiErrorBody;
      if (body.error?.message) message = body.error.message;
    } catch {
      // Keep the stable client message for non-JSON failures.
    }
    throw new ApiRequestError(message, response.status);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
